// ============================================================================
// AgentChatProvider — shared conversation store (Requirement 12)
//
// Single React Context that owns the agent chat state for the entire browser
// tab. All chat surfaces (AutoMateBubble, ChatPanel / SearchPopup) read from
// and dispatch into this store instead of maintaining their own useAgentChat
// instance. The subscription lifecycle is provider-scoped — closing a surface
// mid-stream does NOT abort the in-flight response (Requirement 12.9).
//
// Exports:
//   AgentChatProvider  — wrap at app root (or nearest common ancestor)
//   useAgentChatStore  — hook consumers call instead of useAgentChat
// ============================================================================

import React, { createContext, useContext, useReducer, useRef, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { sendAgentMessage, onAgentResponse } from 'src/utilities/AgentChatAPIs';
import {
  CHAT_AGENT_QUICK_SEARCH_ENABLED,
  AI_AGENT_SHOW_TIMING,
  AI_AGENT_FIRST_TOKEN_WARN_MS,
  AI_AGENT_INTER_CHUNK_WARN_MS,
  AI_AGENT_HARD_TIMEOUT_MS,
} from 'src/Ateam-BI/AutoMate/constants';
import useQuickSearch from 'src/Ateam-BI/AutoMate/hooks/useQuickSearch';
import { createDebugLog } from 'src/Ateam-BI/AutoMate/utils/aiDebugLog';
import { usePersonaContext } from 'src/Ateam-BI/AutoMate/context/PersonaContext';

const debugLog = createDebugLog('[AgentChatProvider]');

// ---------------------------------------------------------------------------
// Persistence constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'ateam:agent-chat:v1';
const SESSION_TTL_MS = 1_800_000; // 30 minutes — matches Bedrock idleSessionTtlInSeconds (Req 4.3)

// ---------------------------------------------------------------------------
// State shape (design.md Section 5a)
// ---------------------------------------------------------------------------

const initialState = {
  sessionId: null,
  messages: [],
  isStreaming: false,
  waitingReason: null, // null | 'first-token' | 'inter-chunk'
  activeConversationId: null,
  expired: false, // true when hydrated state exceeded TTL (Req 12.7)
};

// ---------------------------------------------------------------------------
// Reducer — pure state transitions, no side effects
// ---------------------------------------------------------------------------

function agentChatReducer(state, action) {
  switch (action.type) {
    case 'SUBMIT':
      return {
        ...state,
        messages: [
          ...state.messages,
          { role: 'user', content: action.message, createdAt: Date.now() },
          { role: 'assistant', content: '', createdAt: Date.now() },
        ],
        isStreaming: true,
        waitingReason: null,
        expired: false, // Clear expired flag on new submission
      };

    case 'ACK_RECEIVED':
      return {
        ...state,
        sessionId: action.sessionId || state.sessionId,
        activeConversationId: action.conversationId || state.activeConversationId,
      };

    case 'STREAMING_CHUNK':
      return {
        ...state,
        waitingReason: null,
        messages: updateLastAssistantMessage(state.messages, {
          content: action.content,
          ...(action.timeToFirstTokenMs != null ? { timeToFirstTokenMs: action.timeToFirstTokenMs } : {}),
        }),
      };

    case 'COMPLETE_CHUNK':
      return {
        ...state,
        isStreaming: false,
        waitingReason: null,
        activeConversationId: null,
        messages: updateLastAssistantMessage(state.messages, {
          content: action.content,
          durationMs: action.durationMs,
          status: 'COMPLETE',
          ...(action.sources ? { sources: action.sources } : {}),
          ...(action.correlationId ? { correlationId: action.correlationId } : {}),
        }),
      };

    case 'ERROR_CHUNK':
      return {
        ...state,
        isStreaming: false,
        waitingReason: null,
        activeConversationId: null,
        messages: updateLastAssistantMessage(state.messages, {
          content: action.content,
          durationMs: action.durationMs,
          status: 'ERROR',
        }),
      };

    case 'CANCEL': {
      const msgs = [...state.messages];
      // If the last assistant message has no content, drop it
      if (msgs.length > 0 && msgs[msgs.length - 1].role === 'assistant' && !msgs[msgs.length - 1].content) {
        msgs.pop();
      }
      return {
        ...state,
        messages: msgs,
        isStreaming: false,
        waitingReason: null,
        activeConversationId: null,
      };
    }

    case 'NEW_CHAT':
      // Clear sessionStorage synchronously so a "New Chat" button never
      // leaves stale state on disk (Requirement 12.6)
      try { sessionStorage.removeItem(STORAGE_KEY); } catch (_) { /* quota / private mode */ }
      return {
        ...initialState,
      };

    case 'HYDRATE':
      return {
        ...state,
        sessionId: action.sessionId ?? null,
        messages: action.messages ?? [],
        expired: action.expired ?? false,
      };

    case 'SET_WAITING_REASON':
      return {
        ...state,
        waitingReason: action.reason,
      };

    case 'QUICK_SEARCH_RESULT':
      return {
        ...state,
        messages: updateLastAssistantMessage(state.messages, {
          quickSearchResult: action.result,
        }),
      };

    case 'MUTATION_FAILED': {
      // Remove the empty assistant placeholder on mutation failure
      const filtered = [...state.messages];
      if (filtered.length > 0 && filtered[filtered.length - 1].role === 'assistant' && !filtered[filtered.length - 1].content) {
        filtered.pop();
      }
      return {
        ...state,
        messages: filtered,
        isStreaming: false,
        waitingReason: null,
        activeConversationId: null,
      };
    }

    default:
      return state;
  }
}

/** Helper: update the last assistant message with merged fields */
function updateLastAssistantMessage(messages, updates) {
  if (messages.length === 0) return messages;
  const updated = [...messages];
  const last = updated[updated.length - 1];
  if (last.role !== 'assistant') return messages;
  updated[updated.length - 1] = { ...last, ...updates };
  return updated;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AgentChatContext = createContext(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AgentChatProvider({ children }) {
  const [state, dispatch] = useReducer(agentChatReducer, initialState);

  // ---- User identity for Bedrock long-term memory (memoryId) ----
  // The userId (Cognito alias) serves as a stable per-user identifier so
  // Bedrock can associate cross-session memory with this user when long-term
  // memory is enabled on the agent. Optional — if null/undefined, the backend
  // simply omits memoryId from the invoke_agent call.
  const memoryId = useSelector((globalState) => globalState.auth.userId);

  // ---- Quick search hook (chat path pre-fetch) ----
  const { executeQuickSearch } = useQuickSearch();
  // ---- Persona classification for prompt tailoring ----
  // Consume effectivePersona and papiJobData from PersonaContext so we share
  // the same classification instance as the search flow (Requirement 11.3).
  const { effectivePersona, papiJobData, alias: personaAlias } = usePersonaContext();

  // ---- Provider-scoped refs (moved from per-consumer useAgentChat) ----
  const chunksRef = useRef([]);
  const nextExpectedRef = useRef(0);
  const subscriptionRef = useRef(null);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);
  const warnTimerRef = useRef(null);
  const hardTimerRef = useRef(null);
  const requestStartRef = useRef(0);
  const firstTokenAtRef = useRef(null);

  // We need a ref to track the latest sessionId for use in callbacks
  // (avoids stale closure over state.sessionId in handleChatSubmit)
  const sessionIdRef = useRef(state.sessionId);
  useEffect(() => {
    sessionIdRef.current = state.sessionId;
  }, [state.sessionId]);

  // ---- sessionStorage hydration on mount (Requirement 12.5, 12.7) ----
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return; // key missing — start empty

      const parsed = JSON.parse(raw);

      // Defensive schema validation against manual edits / corrupt storage
      if (
        parsed == null ||
        typeof parsed !== 'object' ||
        typeof parsed.savedAt !== 'number' ||
        !Array.isArray(parsed.messages)
      ) {
        debugLog('hydration: invalid schema, starting empty');
        return;
      }

      const age = Date.now() - parsed.savedAt;

      if (age < SESSION_TTL_MS) {
        // Fresh — restore full state
        debugLog('hydration: fresh state', { age, sessionId: parsed.sessionId });
        dispatch({
          type: 'HYDRATE',
          sessionId: parsed.sessionId ?? null,
          messages: parsed.messages,
          expired: false,
        });
      } else {
        // Expired — clear sessionId but keep transcript with expired marker (Req 12.7)
        debugLog('hydration: expired state', { age });
        dispatch({
          type: 'HYDRATE',
          sessionId: null,
          messages: parsed.messages,
          expired: true,
        });
      }
    } catch (e) {
      // JSON parse failure or sessionStorage access error — start empty
      debugLog('hydration: error reading sessionStorage', e);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount-only

  // ---- Debounced sessionStorage write-through (Requirement 12.5) ----
  const debounceTimerRef = useRef(null);
  useEffect(() => {
    // Clear any pending debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      try {
        const payload = JSON.stringify({
          sessionId: state.sessionId,
          messages: state.messages,
          savedAt: Date.now(),
        });
        sessionStorage.setItem(STORAGE_KEY, payload);
      } catch (e) {
        // Quota exceeded or private mode — best effort
        debugLog('sessionStorage write failed', e);
      }
    }, 100); // 100ms debounce to avoid write amplification during streaming

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [state.sessionId, state.messages]);

  // ---- Timer helpers ----
  const clearTimers = useCallback(() => {
    if (warnTimerRef.current) { clearTimeout(warnTimerRef.current); warnTimerRef.current = null; }
    if (hardTimerRef.current) { clearTimeout(hardTimerRef.current); hardTimerRef.current = null; }
  }, []);

  // ---- Chunk helpers ----
  const resetStreamState = useCallback(() => {
    chunksRef.current = [];
    nextExpectedRef.current = 0;
    firstTokenAtRef.current = null;
  }, []);

  const joinAllChunks = useCallback(() => chunksRef.current.filter((c) => c != null).join(''), []);

  const joinContiguousPrefix = useCallback(() => {
    let out = '';
    const all = chunksRef.current;
    for (let i = 0; i < all.length; i++) {
      if (all[i] == null) break;
      out += all[i];
      nextExpectedRef.current = i + 1;
    }
    return out;
  }, []);

  // ---- Provider mount/unmount lifecycle ----
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimers();
      if (subscriptionRef.current && typeof subscriptionRef.current.unsubscribe === 'function') {
        subscriptionRef.current.unsubscribe();
      }
    };
  }, [clearTimers]);

  // ---- handleChatSubmit ----
  const handleChatSubmit = useCallback((message) => {
    const currentRequestId = ++requestIdRef.current;
    const currentSessionId = sessionIdRef.current;

    // Tear down any previous subscription + timers
    clearTimers();
    if (subscriptionRef.current && typeof subscriptionRef.current.unsubscribe === 'function') {
      subscriptionRef.current.unsubscribe();
    }
    subscriptionRef.current = null;

    dispatch({ type: 'SUBMIT', message });
    resetStreamState();
    requestStartRef.current = performance.now();

    debugLog('handleChatSubmit', {
      requestId: currentRequestId,
      messageLength: message?.length ?? 0,
      sessionId: currentSessionId,
    });

    if (AI_AGENT_SHOW_TIMING) {
      // eslint-disable-next-line no-console
      console.log('[AgentChatProvider] request started', { message });
    }

    // ---- Soft timeout scheduler ----
    const armWarnTimer = (reason, delay) => {
      if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
      warnTimerRef.current = setTimeout(() => {
        if (!mountedRef.current || currentRequestId !== requestIdRef.current) return;
        dispatch({ type: 'SET_WAITING_REASON', reason });
      }, delay);
    };

    // ---- Hard timeout backstop ----
    const armHardTimer = () => {
      if (hardTimerRef.current) clearTimeout(hardTimerRef.current);
      hardTimerRef.current = setTimeout(() => {
        if (!mountedRef.current || currentRequestId !== requestIdRef.current) return;
        if (subscriptionRef.current && typeof subscriptionRef.current.unsubscribe === 'function') {
          subscriptionRef.current.unsubscribe();
        }
        subscriptionRef.current = null;
        clearTimers();
        const durationMs = Math.round(performance.now() - requestStartRef.current);
        dispatch({
          type: 'ERROR_CHUNK',
          content: 'The agent did not respond in time. Please try again.',
          durationMs,
        });
      }, AI_AGENT_HARD_TIMEOUT_MS);
    };

    // Start timers
    armWarnTimer('first-token', AI_AGENT_FIRST_TOKEN_WARN_MS);
    armHardTimer();

    // ---- Timing helpers ----
    const getTimeToFirstToken = () => {
      if (firstTokenAtRef.current != null) return null; // already recorded
      const ttft = Math.round(performance.now() - requestStartRef.current);
      firstTokenAtRef.current = ttft;
      if (AI_AGENT_SHOW_TIMING) {
        // eslint-disable-next-line no-console
        console.log(`[AgentChatProvider] time-to-first-token: ${ttft}ms`);
      }
      return ttft;
    };

    const getDuration = () => {
      const durationMs = Math.round(performance.now() - requestStartRef.current);
      if (AI_AGENT_SHOW_TIMING) {
        // eslint-disable-next-line no-console
        console.log(
          `[AgentChatProvider] time-to-last-token: ${durationMs}ms` +
          (firstTokenAtRef.current != null
            ? ` (TTFT: ${firstTokenAtRef.current}ms, streaming: ${durationMs - firstTokenAtRef.current}ms)`
            : '')
        );
      }
      return durationMs;
    };

    // ---- Fire the mutation ----
    // Requirement 12.2: first turn omits sessionId
    // Requirement 12.3: subsequent turns include sessionId from store
    const fireSendAgentMessage = () => {
    // Requirement 6.1: include PersonaInput with every chatbot mutation
    const personaInput = {
      personaClassification: effectivePersona,
      alias: personaAlias || '',
      jobFamilyName: papiJobData?.jobFamilyName || '',
      jobProfileName: papiJobData?.jobProfileName || '',
      jobLevel: papiJobData?.jobLevel || '',
    };

    sendAgentMessage(message, currentSessionId,
      (result) => {
        // Staleness guard
        if (currentRequestId !== requestIdRef.current) return;
        const { conversationId, sessionId: newSessionId } = result?.data?.sendAgentMessage || {};
        debugLog('sendAgentMessage ack', { conversationId, sessionId: newSessionId });

        // ACK_RECEIVED captures sessionId and opens subscription
        dispatch({ type: 'ACK_RECEIVED', sessionId: newSessionId, conversationId });

        if (conversationId) {
          onAgentResponse(conversationId,
            (response) => {
              // Staleness guard — drop chunks from superseded requests
              if (currentRequestId !== requestIdRef.current) return;
              const chunk = response?.value?.data?.onAgentResponse;
              if (!chunk) return;

              if (chunk.status === 'STREAMING') {
                debugLog('STREAMING chunk', {
                  chunkIndex: chunk.chunkIndex,
                  contentLength: chunk.content?.length ?? 0,
                  content: chunk.content,
                });

                // Re-arm inter-chunk timer
                armWarnTimer('inter-chunk', AI_AGENT_INTER_CHUNK_WARN_MS);

                // Store chunk by index (sparse array, out-of-order tolerant)
                const idx = typeof chunk.chunkIndex === 'number'
                  ? chunk.chunkIndex
                  : chunksRef.current.length;
                chunksRef.current[idx] = chunk.content ?? '';

                // First chunk with content marks TTFT
                let ttft = null;
                if (chunk.content) {
                  ttft = getTimeToFirstToken();
                }

                // Only render contiguous prefix
                const buffered = joinContiguousPrefix();
                dispatch({
                  type: 'STREAMING_CHUNK',
                  content: buffered,
                  ...(ttft != null ? { timeToFirstTokenMs: ttft } : {}),
                });
              } else if (chunk.status === 'COMPLETE') {
                debugLog('COMPLETE chunk', {
                  correlationId: chunk.correlationId,
                  sourceCount: Array.isArray(chunk.sources) ? chunk.sources.length : 0,
                });
                clearTimers();
                const durationMs = getDuration();
                dispatch({
                  type: 'COMPLETE_CHUNK',
                  content: joinAllChunks(),
                  durationMs,
                  sources: Array.isArray(chunk.sources) ? chunk.sources : undefined,
                  correlationId: chunk.correlationId || undefined,
                });
              } else if (chunk.status === 'ERROR') {
                debugLog('ERROR chunk', { content: chunk.content });
                console.error('Agent error:', chunk.content);
                clearTimers();
                const durationMs = getDuration();
                dispatch({
                  type: 'ERROR_CHUNK',
                  content: 'Sorry, something went wrong. Please try again.',
                  durationMs,
                });
              }
            },
            (error) => {
              // Staleness guard
              if (currentRequestId !== requestIdRef.current) return;
              console.error('[AgentChatProvider] subscription error:', error);
              clearTimers();
              const durationMs = getDuration();
              dispatch({
                type: 'ERROR_CHUNK',
                content: 'Sorry, something went wrong. Please try again.',
                durationMs,
              });
            }
          ).then(sub => {
            // Staleness check — if provider unmounted or a newer request fired
            if (!mountedRef.current || currentRequestId !== requestIdRef.current) {
              if (sub && typeof sub.unsubscribe === 'function') sub.unsubscribe();
              return;
            }
            subscriptionRef.current = sub;
          });
        } else {
          // No conversationId — can't subscribe
          clearTimers();
          dispatch({ type: 'MUTATION_FAILED' });
        }
      },
      (error) => {
        // Staleness guard
        if (currentRequestId !== requestIdRef.current) return;
        console.error('[AgentChatProvider] sendAgentMessage error:', error);
        clearTimers();
        dispatch({ type: 'MUTATION_FAILED' });
      },
      // Options bag: pass memoryId so Bedrock can associate long-term memory
      // with this user when memory is enabled on the agent. Pass persona so
      // the backend can tailor responses to the user's technical background.
      { memoryId, persona: personaInput }
    );
    };

    // When the quick search flag is enabled, call executeQuickSearch first
    // with maxResults=1. Insert result into the message flow before streaming
    // content, then proceed to sendAgentMessage regardless of outcome.
    // Skip quick search rendering when result_count is 0.
    if (CHAT_AGENT_QUICK_SEARCH_ENABLED) {
      executeQuickSearch(message, 1).then((quickResult) => {
        if (currentRequestId !== requestIdRef.current) return;
        if (!mountedRef.current) return;
        if (quickResult && quickResult.result_count > 0) {
          dispatch({ type: 'QUICK_SEARCH_RESULT', result: quickResult });
        }
        fireSendAgentMessage();
      });
    } else {
      fireSendAgentMessage();
    }
  }, [clearTimers, resetStreamState, joinAllChunks, joinContiguousPrefix, memoryId, executeQuickSearch, effectivePersona, papiJobData, personaAlias]);

  // ---- handleCancel ----
  const handleCancel = useCallback(() => {
    debugLog('handleCancel');
    // Bump request id so late callbacks are dropped
    requestIdRef.current += 1;
    clearTimers();
    if (subscriptionRef.current && typeof subscriptionRef.current.unsubscribe === 'function') {
      subscriptionRef.current.unsubscribe();
    }
    subscriptionRef.current = null;
    dispatch({ type: 'CANCEL' });
  }, [clearTimers]);

  // ---- handleNewChat (Requirement 12.6) ----
  const handleNewChat = useCallback(() => {
    debugLog('handleNewChat');
    // Bump request id so any in-flight chunks are dropped
    requestIdRef.current += 1;
    clearTimers();
    if (subscriptionRef.current && typeof subscriptionRef.current.unsubscribe === 'function') {
      subscriptionRef.current.unsubscribe();
    }
    subscriptionRef.current = null;
    resetStreamState();
    dispatch({ type: 'NEW_CHAT' });
  }, [clearTimers, resetStreamState]);

  // ---- Context value ----
  const contextValue = {
    messages: state.messages,
    isStreaming: state.isStreaming,
    waitingReason: state.waitingReason,
    sessionId: state.sessionId,
    expired: state.expired,
    handleChatSubmit,
    handleCancel,
    handleNewChat,
  };

  return (
    <AgentChatContext.Provider value={contextValue}>
      {children}
    </AgentChatContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Consumer hook
// ---------------------------------------------------------------------------

/**
 * Access the shared agent chat store. Must be used within an AgentChatProvider.
 *
 * @returns {{
 *   messages: Array,
 *   isStreaming: boolean,
 *   waitingReason: null | 'first-token' | 'inter-chunk',
 *   sessionId: string | null,
 *   expired: boolean,
 *   handleChatSubmit: (message: string) => void,
 *   handleCancel: () => void,
 *   handleNewChat: () => void,
 * }}
 */
export function useAgentChatStore() {
  const context = useContext(AgentChatContext);
  if (context === null) {
    throw new Error(
      'useAgentChatStore must be used within an <AgentChatProvider>. ' +
      'Wrap your component tree with <AgentChatProvider> at the app root.'
    );
  }
  return context;
}

export default AgentChatProvider;
